/* Wabi Blobi interactions. Small, dependency-free custom elements. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* Mobile menu */
  define('wb-header', class extends HTMLElement {
    connectedCallback() {
      const header = this.querySelector('header');
      const btn = this.querySelector('[data-menu-toggle]');
      if (!header || !btn) return;
      btn.addEventListener('click', () => {
        const open = !header.hasAttribute('data-open');
        header.toggleAttribute('data-open', open);
        btn.setAttribute('aria-expanded', String(open));
      });
      window.addEventListener('resize', () => {
        if (window.innerWidth >= 900 && header.hasAttribute('data-open')) {
          header.removeAttribute('data-open');
          btn.setAttribute('aria-expanded', 'false');
        }
      });
    }
  });

  /* Paged carousel: [data-track] holds slides; [data-prev]/[data-next] move it.
     data-per="quotes" -> 1/2/3 per view by width; otherwise 1.
     data-autoplay="ms" advances and wraps; any interaction restarts the timer. */
  define('wb-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.prevBtns = Array.from(this.querySelectorAll('[data-prev]'));
      this.nextBtns = Array.from(this.querySelectorAll('[data-next]'));
      this.index = 0;
      this.prevBtns.forEach((b) => b.addEventListener('click', () => { this.go(this.index - 1); this.restart(); }));
      this.nextBtns.forEach((b) => b.addEventListener('click', () => { this.go(this.index + 1); this.restart(); }));
      this.addEventListener('wb:interact', () => this.restart());
      if (this.dataset.swipe !== 'false') this.bindSwipe();
      window.addEventListener('resize', () => this.update());
      this.update();
      this.restart();
    }
    per() {
      if (this.dataset.per !== 'quotes') return 1;
      const w = window.innerWidth;
      return w < 700 ? 1 : w < 1100 ? 2 : 3;
    }
    max() { return Math.max(0, this.slides.length - this.per()); }
    go(i) { this.index = Math.min(Math.max(0, i), this.max()); this.update(); }
    update() {
      const per = this.per();
      this.style.setProperty('--per', per);
      if (this.index > this.max()) this.index = this.max();
      this.track.style.transform = `translateX(-${(this.index * 100) / per}%)`;
      this.prevBtns.forEach((b) => { b.disabled = this.index === 0; });
      this.nextBtns.forEach((b) => { b.disabled = this.index >= this.max(); });
      this.slides.forEach((s, i) => {
        const visible = i >= this.index && i < this.index + per;
        s.toggleAttribute('inert', !visible);
        s.setAttribute('aria-hidden', String(!visible));
      });
    }
    restart() {
      clearInterval(this.timer);
      const ms = parseInt(this.dataset.autoplay, 10);
      if (!ms || reduceMotion || this.max() === 0) return;
      this.timer = setInterval(() => this.go(this.index >= this.max() ? 0 : this.index + 1), ms);
    }
    bindSwipe() {
      let x0 = null;
      this.track.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.track.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) < 40) return;
        this.go(this.index + (dx < 0 ? 1 : -1));
        this.restart();
      });
    }
    disconnectedCallback() { clearInterval(this.timer); }
  });

  /* Before/after reveal slider */
  define('wb-reveal', class extends HTMLElement {
    connectedCallback() {
      const card = this.querySelector('.wb-ba__card');
      const range = this.querySelector('input[type="range"]');
      if (!card || !range) return;
      const set = () => card.style.setProperty('--pos', `${range.value}%`);
      range.addEventListener('input', () => {
        set();
        this.dispatchEvent(new CustomEvent('wb:interact', { bubbles: true }));
      });
      set();
    }
  });

  /* Two-photo swap with arrows (tiles, audience sections, workshop hero) */
  define('wb-swap', class extends HTMLElement {
    connectedCallback() {
      this.imgs = Array.from(this.querySelectorAll('[data-swap-img]'));
      this.i = 0;
      this.querySelectorAll('[data-swap]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const n = this.imgs.length;
          if (n < 2) return;
          this.i = (this.i + parseInt(btn.dataset.swap, 10) + n) % n;
          this.imgs.forEach((img, k) => { img.hidden = k !== this.i; });
        });
      });
    }
  });

  /* Photo wall: sideways scroll + lightbox */
  define('wb-wall', class extends HTMLElement {
    connectedCallback() {
      const grid = this.querySelector('[data-wall-grid]');
      this.items = Array.from(this.querySelectorAll('[data-wall-item]'));
      this.dialog = this.querySelector('dialog');
      this.big = this.dialog?.querySelector('img');
      const scroll = (d) => grid?.scrollBy({ left: d * grid.clientWidth * 0.9, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.querySelector('[data-wall-prev]')?.addEventListener('click', () => scroll(-1));
      this.querySelector('[data-wall-next]')?.addEventListener('click', () => scroll(1));
      if (!this.dialog || !this.big) return;
      this.items.forEach((btn, i) => btn.addEventListener('click', () => this.open(i)));
      this.dialog.querySelector('[data-lb-prev]').addEventListener('click', (e) => { e.stopPropagation(); this.show(this.i - 1); });
      this.dialog.querySelector('[data-lb-next]').addEventListener('click', (e) => { e.stopPropagation(); this.show(this.i + 1); });
      this.dialog.querySelector('[data-lb-close]').addEventListener('click', (e) => { e.stopPropagation(); this.dialog.close(); });
      this.big.addEventListener('click', (e) => e.stopPropagation());
      this.dialog.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.show(this.i - 1);
        if (e.key === 'ArrowRight') this.show(this.i + 1);
      });
    }
    show(i) {
      const n = this.items.length;
      this.i = (i + n) % n;
      const item = this.items[this.i];
      this.big.src = item.dataset.full;
      this.big.alt = item.querySelector('img')?.alt || '';
    }
    open(i) { this.show(i); this.dialog.showModal(); }
  });

  /* Tabs (FAQ, video tabs). Panels are matched by index. */
  define('wb-tabs', class extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const n = this.tabs.length;
          const j = (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
          this.select(j);
          this.tabs[j].focus();
        });
      });
    }
    select(i) {
      this.tabs.forEach((t, k) => {
        t.setAttribute('aria-selected', String(k === i));
        t.tabIndex = k === i ? 0 : -1;
      });
      this.panels.forEach((p, k) => {
        p.hidden = k !== i;
        if (k !== i) p.querySelectorAll('video').forEach((v) => v.pause());
      });
      const panel = this.panels[i];
      if (panel && this.dataset.openFirst !== undefined) {
        panel.querySelectorAll('details').forEach((d, k) => { d.open = k === 0; });
      }
    }
  });

  /* Exclusive accordion fallback for browsers without <details name> */
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open || !d.dataset.group) return;
    document.querySelectorAll(`details[data-group="${d.dataset.group}"]`).forEach((o) => { if (o !== d) o.open = false; });
  }, true);

  /* Click-to-play video: swaps a poster for the real player held in a <template>. */
  define('wb-video', class extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-play]');
      const tpl = this.querySelector('template');
      if (!btn || !tpl) return;
      btn.addEventListener('click', () => {
        const frame = this.querySelector('[data-frame]') || this;
        frame.querySelectorAll('[data-poster]').forEach((el) => el.remove());
        btn.remove();
        frame.appendChild(tpl.content.cloneNode(true));
        const v = frame.querySelector('video');
        if (v) { v.setAttribute('controls', ''); v.play?.().catch(() => {}); }
      });
    }
  });
})();
